import React, { useEffect, useState, useContext } from 'react'
import { Snackbar, IconButton, withStyles, Button, Grid, Typography, Avatar } from '@material-ui/core'
import { compose } from 'recompose'
import { connect, useSelector, useDispatch } from 'react-redux'
import cn from 'classnames'
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive'
import { MFSDR } from 'Constants/MouseFlow/SensitiveDataReplacer'
import { Notification, NotificationType } from 'types/Notification'
import { useNotifications } from './useNotifications'
import { useNotificationSMSEnabled } from './useNotificationSMSEnabled'
import createNotificationSelector from 'State/Store/Notification/Notification.selector'
import { useHistory } from 'react-router-dom'
import { createOrderPageLink } from 'Pages/Order/Order.page.route'
import ServicesContext from 'Contexts/Services/Services.context'
import NotificationActionCreatorsMapping from 'State/Store/Notification/NotificationActionCreatorCenter'
import hitGTM from 'State/Store/UI/Actions/GTM.action'
import { UI_NOTIFICATION_BANNER_GO_TO_ORDER, UI_NOTIFICATION_BANNER_DISMISS, UI_NOTIFICATIONS_LAST_TRUCK_INAPP_VIEW_ORDER_BTN, UI_NOTIFICATIONS_LAST_TRUCK_INAPP_DISMISS } from 'State/Store/UI/UI.types'
import { getDDMMYYYY } from 'ui-format-utils.ts/get-dd-mm-yyyy'
import { withGlobalStyles } from 'Support/Theme/CreateInjectableStyles/withGlobalStyles'
import GlobalBannerStyles from './GlobalBanner.style'
import { WithGlobalStylesProps } from 'types/styles/WithGlobalStylesProps'
import { NotificationAction } from 'State/Store/Notification/Notification.reducer'

interface Props {
  any?: number,
}

type FProps = Props & WithGlobalStylesProps<any>

const GlobalBanner = (props: FProps) => {
  const {
    classes,
  } = props
  const [ activeNotification, setActiveNotification ] = useState<Notification | null>(null)

  const [ enabledSMS, enabledSMSLoaded, setEnabledSMS ] = useNotificationSMSEnabled()
  const { notifications } = useSelector(createNotificationSelector)
  const history = useHistory()
  const dispatch = useDispatch()
  const services = useContext(ServicesContext)
  const { refresh: refreshNotifications } = useNotifications(activeNotification)

  useEffect(() => {
    if (enabledSMSLoaded) {
      // make sure to display SMS Enabling for the very first time
      if (!enabledSMS) {
        setActiveNotification({
          type: NotificationType.SYSTEM_NOTIFICATION_SETTINGS_AVAILABLE,
        } as Notification)
      } else {
        let oldestCurrenAndUnreadNotifications: undefined | Notification
        // find the oldest current and not read
        for (let index = notifications.length - 1; index >= 0; index--) {
          const notification = notifications[index];
          if (notification.isCurrent && !notification.isRead
            && ![
              NotificationType.USER_REMOVE,
              NotificationType.USER_ROLE_DOWNGRADE,
              NotificationType.USER_ROLE_UPGRADE,
            ].includes(notification.type)
          ) {
            oldestCurrenAndUnreadNotifications = notification
            break
          }
        }
        if (oldestCurrenAndUnreadNotifications) {
          setActiveNotification(oldestCurrenAndUnreadNotifications)
        } else {
          // nothing to display
          setActiveNotification(null)
        }
      }
    }
  }, [notifications, enabledSMS, enabledSMSLoaded])

  if (activeNotification === null) {
    return null
  }

  const getContent = (notification: Notification) => {
    const { type, deliveryAddress, plantName, orderDate, truckCode } = notification
    if ([
      NotificationType.USER_REMOVE,
      NotificationType.USER_ROLE_DOWNGRADE,
      NotificationType.USER_ROLE_UPGRADE,
    ].includes(type)) {
      return null
    }
    const mappings: any = {
      FIRST_LOAD: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          The first truck for <span className={classes.fontWeight500}>{deliveryAddress}</span> has left{` `}
          <span className={classes.fontWeight500}>{plantName}</span> and is on its way
        </Typography>
      ),
      ORDER_BOT_SUCCESS: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          Your online order to <span className={classes.fontWeight500}>{deliveryAddress}</span> has been{` `}
          accepted
        </Typography>
      ),
      ORDER_BOT_IN_PROCESS: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          There is a delay in accepting your online order to <span className={classes.fontWeight500}>{deliveryAddress}</span>. Further
          processing may take up to 1 business day.
        </Typography>
      ),
      ORDER_MODIFICATION_PROCESSING: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          {
            `There has been a delay in modifying your order to ${deliveryAddress} on ${orderDate && getDDMMYYYY(orderDate)}. Further processing may take up to 1 business day.`
          }
        </Typography>
      ),
      ORDER_MODIFICATION_ACCEPTED: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          {
            `Your order to ${deliveryAddress} on ${orderDate && getDDMMYYYY(orderDate)} has been modified. Boral may additionally review this within 1 business day. We will call you if there’s a problem.`
          }
        </Typography>
      ),
      ORDER_MODIFICATION_ACCEPTED_WITH_CONSULT: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          {
            `Your order to ${deliveryAddress} on ${orderDate && getDDMMYYYY(orderDate)} has been modified by Boral in consultation with the customer.`
          }
        </Typography>
      ),
      LAST_LOAD: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          {
            `The last truck for ${deliveryAddress} has left ${plantName} and is on its way. The truck number is ${truckCode}.`
          }
        </Typography>
      ),
      LAST_LOAD_WITH_PLUS_LOAD: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          {
            `The last truck for ${deliveryAddress} has left ${plantName} and is on its way. The truck number is ${truckCode} and it is carrying a Plus Load.`
          }
        </Typography>
      ),

      // for system
      SYSTEM_NOTIFICATION_SETTINGS_AVAILABLE: (
        <Typography variant='body2' data-mf-replace-inner={MFSDR}>
          {
            `You can now choose to receive notifications like this by SMS.`
          }
        </Typography>
      ),
    }
    return mappings[type]
  }
  const getFirstButton = (notification: Notification) => {
    const { type, deliveryAddress, plantName } = notification
    if ([
      NotificationType.USER_REMOVE,
      NotificationType.USER_ROLE_DOWNGRADE,
      NotificationType.USER_ROLE_UPGRADE,
    ].includes(type)) {
      return ''
    }
    const mappings: any = {
      FIRST_LOAD: `View order`,
      ORDER_BOT_SUCCESS: `View order`,
      ORDER_BOT_IN_PROCESS: `View order`,
      ORDER_MODIFICATION_PROCESSING: `View order`,
      ORDER_MODIFICATION_ACCEPTED: `View order`,
      ORDER_MODIFICATION_ACCEPTED_WITH_CONSULT: `View order`,
      LAST_LOAD: `View order`,
      LAST_LOAD_WITH_PLUS_LOAD: `View order`,
      // for system
      SYSTEM_NOTIFICATION_SETTINGS_AVAILABLE: `View Settings`,
    }
    return mappings[type]
  }
  const content = getContent(activeNotification)
  const firstButton = getFirstButton(activeNotification)
  const onFirstButtonClick = async () => {
    const { type, deliveryAddress, plantName, orderKey } = activeNotification
    if ([ NotificationType.SYSTEM_NOTIFICATION_SETTINGS_AVAILABLE ].includes(type)) {
      await services.notificationService.markSMSEnablingChecked()
      setEnabledSMS(true)
      dispatch(NotificationActionCreatorsMapping.openNotificationModal())
      dispatch(NotificationActionCreatorsMapping.toggleNotificationSettings())
    } else {
      if ([ NotificationType.LAST_LOAD, NotificationType.LAST_LOAD_WITH_PLUS_LOAD ].includes(type)) {
        dispatch(hitGTM(UI_NOTIFICATIONS_LAST_TRUCK_INAPP_VIEW_ORDER_BTN))
      }
      dispatch(hitGTM(UI_NOTIFICATION_BANNER_GO_TO_ORDER))
      await markItRead()
      history.push(createOrderPageLink({ orderId: orderKey }))
    }
  }
  const onSecondButtonClick = async () => {
    dispatch(hitGTM(UI_NOTIFICATION_BANNER_DISMISS))
    const { type, deliveryAddress, plantName } = activeNotification
    if ([ NotificationType.SYSTEM_NOTIFICATION_SETTINGS_AVAILABLE ].includes(type)) {
      await services.notificationService.markSMSEnablingChecked()
      setEnabledSMS(true)
    } else {
      if ([ NotificationType.LAST_LOAD, NotificationType.LAST_LOAD_WITH_PLUS_LOAD ].includes(type)) {
        dispatch(hitGTM(UI_NOTIFICATIONS_LAST_TRUCK_INAPP_DISMISS))
      }
      await markItRead()
    }
  }
  const markItRead = async () => {
    try {
      await services.notificationService
        .markIdRead(activeNotification.id)
      refreshNotifications()
    } catch (e) {
      //
    }
  }
  return (
    <React.Fragment>
      <Grid container className={cn(classes.banner /* bannerClasses */)}>
        <Grid item xs={12}>
          <Grid container spacing={2} className={classes.flexWrapNowrap}>
            <Grid item>
              <Avatar className={classes.bannerIcon}>{<NotificationsActiveIcon />}</Avatar>
            </Grid>
            <Grid item className={classes.flexGrow1} data-mf-replace-inner={MFSDR}>
              {content}
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item className={cn(classes.flexGrow1, classes.textAlignRight)}>
              <Button color='primary' variant='contained' onClick={onFirstButtonClick}>
                { firstButton }
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={onSecondButtonClick}>Dismiss</Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

export default compose<FProps, Props>(
  withGlobalStyles(GlobalBannerStyles),
)(GlobalBanner)
